import {isStaffOnlyRoleSet} from "../../shared/models/roles";
import {effectiveStaffVisibility, resolveStaffVisibilityRules, STAFF_VISIBILITY_AREAS, StaffVisibilityArea, StaffVisibilityLevel} from "../../shared/models/staffVisibility";
import {useGetStaffVisibilityRulesQuery} from "../generated/graphql";
import {useCurrentUserRoles} from "../helpers/useIsStaffOnlyAccess";

const AREAS = Object.keys(STAFF_VISIBILITY_AREAS) as Array<StaffVisibilityArea>;

// Regeln + Rollen des Logins. Nur Mitarbeitenden-Zugänge fragen die Regeln ab.
const useStaffRules = () => {
    const {roles} = useCurrentUserRoles();
    const isStaff = isStaffOnlyRoleSet(roles);
    const {data, loading} = useGetStaffVisibilityRulesQuery({skip: !isStaff});

    return {roles, pending: isStaff && loading, rules: resolveStaffVisibilityRules(data?.staffVisibilityRules)};
};

/**
 * Sichtbarkeitsstufe des eingeloggten Logins für einen Bereich (#707).
 *
 * `null` = kein Mitarbeitenden-Zugang (Admin und höher) → nie eingeschränkt; dann läuft
 * auch keine Abfrage. Solange Rollen oder Regeln laden oder die Abfrage scheitert, kommt
 * `null` bzw. die Default-Stufe: bei Default ändert sich also nichts. Durchgesetzt wird
 * auf dem Server, der Hook steuert nur Navigation und Hinweise.
 */
export const useStaffVisibility = (area: StaffVisibilityArea): StaffVisibilityLevel | null => {
    const {roles, rules} = useStaffRules();

    return effectiveStaffVisibility(rules, area, roles);
};

/** Bereiche, die für den Login auf „Kein Zugriff“ stehen (#709, Menü). Während des Ladens leer. */
export const useHiddenStaffAreas = (): Array<StaffVisibilityArea> => {
    const {roles, rules} = useStaffRules();

    return AREAS.filter((area) => effectiveStaffVisibility(rules, area, roles) === StaffVisibilityLevel.NoAccess);
};

export type StaffAreaAccess = "visible" | "pending" | "hidden";

/**
 * Für Abfragen eines Bereichs außerhalb seiner Seite, etwa Filter-Auswahllisten (#709):
 * „hidden“ bei „Kein Zugriff“ → nicht abfragen, Auswahl ausblenden; „pending“, solange
 * die Regeln eines Mitarbeitenden-Zugangs laden → noch nicht abfragen (sonst feuert die
 * Abfrage vor der Regel und scheitert mit Forbidden). Admin: sofort „visible“, ohne
 * Regel-Abfrage. Scheitert die Regel-Abfrage, gilt der Default („visible“).
 */
export const useStaffAreaAccess = (area: StaffVisibilityArea): StaffAreaAccess => {
    const {roles, rules, pending} = useStaffRules();

    if (pending) {
        return "pending";
    }

    return effectiveStaffVisibility(rules, area, roles) === StaffVisibilityLevel.NoAccess ? "hidden" : "visible";
};
